import { useCallback } from 'react';
import {
  useAgentsQuery,
  useCreateSessionMutation,
  useSendMessageMutation,
} from '../api';
import { AgentForgeApiError } from '../api/http';
import { useAgentForgeStore } from '../stores';
import type { ChatSubmit } from './useChatSubmit.types';

const isStaleSessionError = (err: unknown): boolean =>
  err instanceof AgentForgeApiError && err.status === 404;

const DEFAULT_AGENT_SLUG = 'pipeline-manager';

// True while the active session has a turn in flight. Sends are blocked for
// its whole duration: the store keys streaming state (draft, sub-steps,
// status) by session only, so a second concurrent turn on the same session
// makes the first stream_done wipe the second turn's in-progress UI.
// Serializing turns here — the one choke point every send path (composer,
// chips, clarify forms) goes through — is what keeps that state coherent.
const activeSessionStreaming = (
  s: ReturnType<typeof useAgentForgeStore.getState>
): boolean => {
  const sid = s.activeSessionId;
  if (!sid) return false;
  const tab = s.openTabs.find((t) => t.sessionId === sid);
  return tab?.status === 'streaming' || s.streamingDraft?.sessionId === sid;
};

// Composes "resolve-or-create session → append user msg → mark streaming".
// Each consumer wires the returned `send` into Composer.onSubmit. The real
// path POSTs to `/sessions/{id}/messages` and flips the tab status to
// `streaming`. The reply lands via V1's WebSocket (`useAgentForgeStream`)
// and the canonical record is fetched by `useAgentForgeMessages` after
// `stream_done`.
//
// `send` returns the sessionId so callers can navigate to a per-session
// route after a fresh session is created. Returns `null` on empty input
// or session-resolution failure.
//
// `activeSessionId` is read via `useAgentForgeStore.getState()` inside the
// callback (not via a closing selector) so callers that openTab a
// pre-warmed session synchronously *before* calling `send` see the new
// active session in the same tick — without the callback having to
// recreate first.
export const useChatSubmit = (): ChatSubmit => {
  const openTab = useAgentForgeStore((s) => s.openTab);
  const closeTab = useAgentForgeStore((s) => s.closeTab);
  const setMode = useAgentForgeStore((s) => s.setMode);
  const appendUserMessage = useAgentForgeStore((s) => s.appendUserMessage);
  const setTabStatus = useAgentForgeStore((s) => s.setTabStatus);

  const agentsQuery = useAgentsQuery();
  const createSessionMutation = useCreateSessionMutation();
  const sendMessage = useSendMessageMutation();

  // Reactive mirror of the send() guard below, so consumers' disabled
  // states track the stream, not just the POST.
  const isStreaming = useAgentForgeStore(activeSessionStreaming);

  const send = useCallback(
    async (text: string, files?: File[]): Promise<string | null> => {
      const trimmed = text.trim();
      const attachments = files ?? [];
      if (trimmed.length === 0 && attachments.length === 0) return null;

      // One turn at a time per session (read fresh state — the callback may
      // outlive the render that created it).
      if (activeSessionStreaming(useAgentForgeStore.getState())) return null;

      // Optimistic bubble text for a file-only submit (no typed message).
      const optimisticText =
        trimmed.length > 0
          ? trimmed
          : `Uploaded ${attachments.map((f) => f.name).join(', ')}`;

      const activeSessionId = useAgentForgeStore.getState().activeSessionId;

      const startFreshSession = async (): Promise<string | null> => {
        const agents = agentsQuery.data ?? [];
        const agent =
          agents.find((a) => a.slug === DEFAULT_AGENT_SLUG) ?? agents[0];
        if (!agent) return null;
        const session = await createSessionMutation.mutateAsync({
          agent_id: agent.id,
          title: optimisticText,
        });
        openTab({
          sessionId: session.id,
          title: session.title || optimisticText,
          status: 'idle',
          createdAt: session.created_at,
        });
        setMode('floating');
        return session.id;
      };

      const stampOptimistic = (id: string) => {
        appendUserMessage(id, optimisticText);
        // setTabStatus is a no-op when the session has no tab entry — and
        // sessions opened via setActive (deep link, history list) never
        // called openTab. The turn then rendered with NO thinking indicator
        // until the first WS frame arrived ("the activity is lost"). Upsert
        // the tab before stamping it so isAwaitingReply flips immediately.
        const tabs = useAgentForgeStore.getState().openTabs;
        if (!tabs.some((t) => t.sessionId === id)) {
          openTab({
            sessionId: id,
            title: optimisticText,
            status: 'idle',
            createdAt: new Date().toISOString(),
          });
        }
        setTabStatus(id, 'streaming');
      };

      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

      let sessionId = activeSessionId;
      if (!sessionId) {
        try {
          sessionId = await startFreshSession();
        } catch {
          return null;
        }
        if (!sessionId) return null;
      }

      stampOptimistic(sessionId);

      try {
        await sendMessage.mutateAsync({
          sessionId,
          message: trimmed,
          timezone: tz,
          files: attachments,
        });
        return sessionId;
      } catch (err) {
        if (!isStaleSessionError(err)) {
          setTabStatus(sessionId, 'error');
          return sessionId;
        }
      }

      // Stale persisted session — backend doesn't know it. Drop the dead tab
      // (also clears its optimistic messages), create a fresh one, retry once.
      closeTab(sessionId);
      let recoveredId: string | null = null;
      try {
        recoveredId = await startFreshSession();
      } catch {
        return null;
      }
      if (!recoveredId) return null;
      stampOptimistic(recoveredId);
      try {
        await sendMessage.mutateAsync({
          sessionId: recoveredId,
          message: trimmed,
          timezone: tz,
          files: attachments,
        });
      } catch {
        setTabStatus(recoveredId, 'error');
      }
      return recoveredId;
    },
    [
      openTab,
      closeTab,
      setMode,
      appendUserMessage,
      setTabStatus,
      agentsQuery.data,
      createSessionMutation,
      sendMessage,
    ]
  );

  return {
    send,
    isSending:
      createSessionMutation.isPending || sendMessage.isPending || isStreaming,
  };
};
